import { getAudience, getConfig } from "@/components/config";
import { IImportJsonBlocks } from "@/store/importBlocksAndAllocationsStore";
import {
  refreshUnassignedBlocks,
  updateBlockAction,
} from "@/store/importBlocksAndAllocationsStoreACTIONS";
import { updateSinglePriceInStore } from "@/store/priceDataStoreACTIONS";
import { updateBlockInfoESID } from "@/store/tradeBlockDataStoreACTIONS";
import tradeDataStore from "@/store/tradeDataStore";
import {
  getAllActionSummaryData,
  updateActionSummaryForESID,
  updateIndicativeMidForESID,
} from "@/store/tradeDataStoreACTIONS";
import { refreshRequestInformationForESID } from "@/store/requestInformationStoreACTIONS";
import { useAuth0 } from "@auth0/auth0-react";
import {
  HubConnectionBuilder,
  HubConnection,
  LogLevel,
} from "@microsoft/signalr";
import { MessagePackHubProtocol } from "@microsoft/signalr-protocol-msgpack";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { ExecutionStrategyStatus } from "./interfaces and Enums/ENUMExecutionStrategyStatus";
import { IPriceSummaryModel } from "./interfaces and Enums/InterfacePriceSummary";

const MegaPipe = () => {
  const config = getConfig();

  const [esidEvent, setEsidEvent] = useState("");
  const [connections, setConnections] = useState<HubConnection[]>([]);
  // const debounceEsid = useDebounce(esidEvent, 500);
  const { getAccessTokenSilently } = useAuth0();

  useEffect(() => {
    setUpSignalRConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (esidEvent != "") {
      //const indexOfPipe = debounceEsid.indexOf("|");
      //const esid = debounceEsid.substring(indexOfPipe + 1);
      updateActionSummaryForESID(esidEvent, getAccessTokenSilently);

      setEsidEvent("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esidEvent]);

  const setUpSignalRConnection = async () => {
    const tradeConnection = new HubConnectionBuilder()
      .withUrl(config.megaPipeTradesUrl, {
        accessTokenFactory: () => getAccessToken(),
      })
      .withAutomaticReconnect()
      .withHubProtocol(new MessagePackHubProtocol())
      .configureLogging(LogLevel.Warning)
      .build();

    tradeConnection.on("TradeImported", () => {
      updateBlocks();
    });

    tradeConnection.on("TradeSaved", (esid) => {
      setEsidEvent(esid);
      //updateActionSummary();
      updateBlocks();
    });

    tradeConnection.on("TradeSent", (esid) => {
      setEsidEvent(esid);
      //updateActionSummaryForESID(esid, getAccessTokenSilently);
    });

    tradeConnection.on("TradeReceived", (esid) => {
      setEsidEvent(esid);
      //updateActionSummaryForESID(esid, getAccessTokenSilently);
    });

    tradeConnection.on("TradeUpdated", (esid) => {
      //setEsidEvent(esid);
      updateActionSummaryForESID(esid, getAccessTokenSilently);
    });

    tradeConnection.on("TradeLevelsNegotiation", (esid) => {
      // event occurs when level negotiation has initiated or level countered
      updateActionSummaryForESID(esid, getAccessTokenSilently);
    });

    tradeConnection.on("TradeLevelsAccepted", (esid) => {
      // event occurs when levels have been accepted during order level negotiation
      updateActionSummaryForESID(esid, getAccessTokenSilently);
    });

    tradeConnection.on("TradeBlocksUpdated", (esid) => {
      //setEsidEvent(esid);
      const currentTrade = tradeDataStore
        .getState()
        .tradeData.find((trade) => trade.esid == esid);
      const currentRoleForTrade = currentTrade?.role;

      if (currentRoleForTrade && currentRoleForTrade == "RP") {
        updateActionSummaryForESID(esid, getAccessTokenSilently);
      } else {
        if (currentTrade?.status != ExecutionStrategyStatus.New) {
          updateActionSummaryForESID(esid, getAccessTokenSilently); // OP still needs update on the action summary for SWPs that may have their status of ReCoupon changed through the trade cycle.
        }
        updateBlockInfoESID(esid, getAccessTokenSilently);
      }
    });

    tradeConnection.on("TradeIndicativeComplete", (esid) => {
      updateIndicativeMidForESID(esid, getAccessTokenSilently);
    });

    // LQC-637 — RP/OP "Request Information" negotiation. PLACEHOLDER event name:
    // confirm the actual SignalR message the backend raises when a request is
    // made / responded to, then rename. Re-fetches just this esid's request
    // state from /Trade/Execution/Requests/{esid} into the standalone store.
    tradeConnection.on("TradeRequestInfoUpdated", (esid) => {
      refreshRequestInformationForESID(esid, getAccessTokenSilently);
    });

    tradeConnection.on("TradeRefreshAll", () => {
      updateActionSummary();
    });

    tradeConnection.on("TradeRefreshBrowser", () => {
      window.location.reload();
    });

    const priceConnection = new HubConnectionBuilder()
      .withUrl(config.megaPipePricesUrl, {
        accessTokenFactory: () => getAccessToken(),
      })
      .withAutomaticReconnect()
      .withHubProtocol(new MessagePackHubProtocol())
      .configureLogging(LogLevel.Warning)
      .build();

    priceConnection.on("PriceImported", (data: string) => {
      const model: IPriceSummaryModel = JSON.parse(data);
      updateSinglePriceInStore(model);
    });

    const blockConnection = new HubConnectionBuilder()
      .withUrl(config.megaPipeBlocksUrl, {
        accessTokenFactory: () => getAccessToken(),
      })
      .withAutomaticReconnect()
      .withHubProtocol(new MessagePackHubProtocol())
      .configureLogging(LogLevel.Warning)
      .build();

    blockConnection.on("BlockUpdated", (data: string) => {
      const model: IImportJsonBlocks = JSON.parse(data);
      updateBlockAction(model);
    });

    blockConnection.on("BlockCancelled", () => {
      // toast will run twice in local, not in DEV and above.
      toast.success("Block deleted from Trade Creation.");
      updateBlocks();
    });

    // const axeConnection = new HubConnectionBuilder()
    //   .withUrl(config.megaPipeAxesUrl, {
    //     accessTokenFactory: () => getAccessToken(),
    //   })
    //   .withAutomaticReconnect()
    //   .withHubProtocol(new MessagePackHubProtocol())
    //   .configureLogging(LogLevel.Warning)
    //   .build();

    // axeConnection.on("AxesCreated", (data: string) => {
    //   const model: IAxeCreationData[] = JSON.parse(data);
    //   addAxeToStore(model);
    // });

    setConnections([tradeConnection, priceConnection, blockConnection]);
  };

  useEffect(() => {
    const startConnections = async () => {
      try {
        for (let index = 0; index < connections.length; index++) {
          await connections[index].start();
        }
      } catch {}
    };

    const stopConnections = async () => {
      try {
        for (let index = 0; index < connections.length; index++) {
          await connections[index]
            .stop()
            .then(() => console.log(`Disconnected MegaPipe ${index + 1}`))
            .catch((err) =>
              console.error(`Error during MegaPipe cleanup ${index + 1}`, err),
            );
        }
      } catch {}
    };

    startConnections();

    return () => {
      stopConnections();
    };
  }, [connections]);

  const getAccessToken = async () => {
    const accessToken = await getAccessTokenSilently({
      authorizationParams: {
        audience: `${getAudience()?.audience}`,
        scope: "openid profile email",
      },
    });
    return accessToken;
  };

  // Body moved to refreshUnassignedBlocks so the Trade From Scratch send can reuse it.
  const updateBlocks = async () => {
    await refreshUnassignedBlocks(getAccessTokenSilently);
  };

  const updateActionSummary = async () => {
    await getAllActionSummaryData(getAccessTokenSilently);
  };

  return false;
};

export default MegaPipe;
